const apiBaseUrl = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ""
).replace(/\/+$/, "");

// Pathname portion of the base URL — used for same-origin path matching (e.g. session checks).
export const apiBasePath: string = (() => {
  if (!apiBaseUrl) return "/api";
  try {
    return new URL(apiBaseUrl).pathname.replace(/\/+$/, "") || "/api";
  } catch {
    return apiBaseUrl.replace(/\/+$/, "") || "/api";
  }
})();

export function buildRequestUrl(path: string): string {
  const normalizedPath = path.replace(/^\/+/, "");
  const base = apiBaseUrl || `${window.location.origin}/api`;
  return `${base}/${normalizedPath}`;
}

export type ApiFetchClientResponseEvent = {
  path: string;
  requestUrl: URL;
  requestInit: RequestInit;
  response: Response;
};

export type ApiFetchClientResponseListener = (
  event: ApiFetchClientResponseEvent,
) => void | Promise<void>;

const apiFetchClientResponseListeners =
  new Set<ApiFetchClientResponseListener>();

export function subscribeApiFetchClientResponses(
  listener: ApiFetchClientResponseListener,
) {
  apiFetchClientResponseListeners.add(listener);
  return () => {
    apiFetchClientResponseListeners.delete(listener);
  };
}

export async function apiFetchClient(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const requestUrl = new URL(buildRequestUrl(path));
  const requestInit: RequestInit = { ...options, credentials: "include" };
  const response = await fetch(requestUrl.toString(), requestInit);

  if (apiFetchClientResponseListeners.size > 0) {
    const event: ApiFetchClientResponseEvent = {
      path,
      requestUrl,
      requestInit,
      response,
    };
    for (const listener of apiFetchClientResponseListeners) {
      try {
        await listener(event);
      } catch {
        // Ignore listener failures to avoid breaking API calls.
      }
    }
  }

  return response;
}
